import {
  forEach,
  some,
  get,
  pickBy,
  isEmpty,
  map,
  filter,
  find,
  head,
  compact,
  uniqBy,
  uniq,
} from 'lodash'
import React, { Component } from 'react'
import { compose } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import Fuse from 'fuse.js'

import { ORG_NAME } from 'utils/env'
import { formattedAutocompleteList } from 'utils/configurations'
import { isNumberOrDot } from 'utils/validators'
import ItemsEdit from 'containers/ItemsEdit'
import DropdownSelect from 'components/DropdownSelect'
import Textbox from 'components/Textbox'
import Autocomplete from 'components/Autocomplete'
import DeletePrinterModal from 'components/DeletePrinterModal'
import { getPrinter, getPrinters, getUsbBluetoothTcpPrinters } from 'store/Printers/selector'
import { getPrintControllerConfigurations } from 'store/Configurations/selector'
import { deletePrinter, fetchPrinters } from 'store/Printers/actions'
import { getCurrentVenueId } from 'store/Venues/selector'
import ToggleWithLabel from 'components/ToggleWithLabel'

const SELECTABLE_PRINTERS = [
  {
    value: 'epson thermal',
    label: 'Epson Thermal',
  },
  {
    value: 'epson impact',
    label: 'Epson Impact',
  },
  {
    value: 'star micronics thermal',
    label: 'Star Micronics Thermal',
  },
  {
    value: 'star micronics impact',
    label: 'Star Micronics Impact',
  },
]

const SELECTABLE_MODELS = [
  {
    value: null,
    label: 'None',
  },
  {
    value: 'TM-T88VI',
    label: 'TM-T88VI',
  },
]

const SELECTABLE_CONNECTIONS = [
  {
    value: 'TCP',
    label: 'IP',
  },
  {
    value: 'USB',
    label: 'USB',
  },
  {
    value: 'Bluetooth',
    label: 'Bluetooth',
  },
]

const FUSE_OPTIONS = {
  threshold: 0.1,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  minMatchCharLength: 1,
  keys: ['label'],
}

export class PrintersForm extends Component {
  static propTypes = {
    printers: PropTypes.array,
    fetchPrinters: PropTypes.func,
    configurations: PropTypes.array,
    editedItem: PropTypes.object,
    printersUsbBluetoothTcp: PropTypes.array,
    deletePrinter: PropTypes.func.isRequired,
    venueUuid: PropTypes.string.isRequired,
    title: PropTypes.string,
    callApi: PropTypes.func.isRequired,
    callConfiguration: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        id: PropTypes.string,
      }).isRequired,
    }).isRequired,
  }

  constructor(props) {
    super(props)
    this.state = {
      isModalOpen: false,
      name: get(props, 'editedItem.name', ''),
      printerType: get(props, 'editedItem.printerType') || SELECTABLE_PRINTERS[0].value,
      printerModel: get(props, 'editedItem.printerModel') || SELECTABLE_MODELS[0].value,
      connection: get(props, 'editedItem.connection') || SELECTABLE_CONNECTIONS[0].value,
      port: get(props, 'editedItem.port', ''),
      printController: get(props, 'editedItem.configuration'),
      backupPort: get(props, 'editedItem.backupPort') || '',
      receiptPrinter: get(props, 'editedItem.receiptPrinter', false),
    }
  }

  resourceId = () => this.props.match.params.id

  redirectToPrinters = () => {
    this.props.history.push(`/printers`)
  }

  handleChange = (event) => {
    const { name, value } = event.target
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  handleConnectionChange = (event) => {
    const { name, value } = event.target
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))

    this.setState({
      port: '',
    })
  }

  handleCheckboxChange = (event) => {
    this.setState({
      receiptPrinter: event.target.checked,
    })
  }

  handleSubmit = (event) => {
    event.preventDefault()

    const {
      name,
      printerType,
      printerModel,
      connection,
      port,
      controller,
      backupPort,
      receiptPrinter,
      printController,
    } = this.state

    const {
      venueUuid,
      configurations,
      printers,
      editedItem: { uuid, configurationControlledPrinters, port: existingPort },
    } = this.props

    const printerAttributes = {
      name,
      printerType,
      printerModel,
      connection,
      port,
      controller,
      backupPort,
    }

    const newUuid = uuidv4()

    const printersPorts = compact(printers?.map((printer) => printer.port))
    const isPortTaken = printersPorts.includes(port) && port !== existingPort

    // set of data neccessary to save printer in app_configuration
    const selectedConfiguration = find(
      configurations,
      (configuration) => configuration.uuid === head(printController).value,
    )

    const configurationAttributes = {
      configuration: {
        // this is neccessary to prevent clear data connected to configuration
        // we need to save them once again, otherwise they disappear
        ...selectedConfiguration.configuration,
        controlledPrinterUuids: compact(
          uniq([
            ...(get(selectedConfiguration, 'configuration.controlledPrinterUuids') || []),
            this.resourceId() ? uuid : newUuid,
          ]),
        ),
      },
      uuid: head(printController).value,
      organizationName: ORG_NAME,
      venueUuid,
    }

    // delete current printer from printers array from previous app_configuration
    // these filtered array is neccessary to send request to unlink
    // current printer from previous app_configuration
    const controlledPrintersWithoutPreviousPrintController = filter(
      configurationControlledPrinters.controlledPrinterUuids,
      (printer) => printer !== uuid,
    )

    // set of data neccessary delete printer
    const unlinkConfigurationAttributes = {
      configuration: {
        // this is neccessary to prevent clear data connected to configuration
        // we need to save them once again, otherwise they disappear
        ...configurationControlledPrinters,
        controlledPrinterUuids: controlledPrintersWithoutPreviousPrintController,
      },
      uuid: head(this.props.editedItem.configuration).value,
      organizationName: ORG_NAME,
      venueUuid,
    }

    const formatedPrinterAttributes = pickBy(
      printerAttributes,
      (value, key) => !isEmpty(value) || key === 'backupPort',
    )

    const { callApi, callConfiguration } = this.props
    formatedPrinterAttributes.uuid = this.resourceId() || newUuid
    formatedPrinterAttributes.venueUuid = venueUuid
    formatedPrinterAttributes.receiptPrinter = receiptPrinter

    callApi({ printers: [formatedPrinterAttributes] })
      .then(() => {
        if (isEmpty(configurationControlledPrinters.controlledPrinterUuids)) {
          callConfiguration({
            appDeviceConfigurations: [configurationAttributes],
          })
        } else {
          callConfiguration({
            appDeviceConfigurations: [unlinkConfigurationAttributes],
          }).then(() => {
            callConfiguration({
              appDeviceConfigurations: [configurationAttributes],
            })
          })
        }
      })
      .then(() => {
        if (this.resourceId()) {
          toast.success('Printer succesfully updated')
        } else {
          toast.success('Printer succesfully created')
        }

        if (isPortTaken) {
          toast.warn(
            'The IP address you entered already exists. Please ensure the IP address is unique to avoid network conflicts.',
          )
        }
        this.redirectToPrinters()
      })
  }

  handleDeletePrinter = () => {
    const {
      editedItem,
      editedItem: { configurationsWithSetReceiptPrinterName },
      editedItem: { configurationWithAssociatedPrinter },
      deletePrinter,
      callConfiguration,
      fetchPrinters,
      venueUuid,
    } = this.props
    this.toggleModal()

    const allConfiugrationsForUpdate = compact(
      uniqBy(
        [...configurationsWithSetReceiptPrinterName, configurationWithAssociatedPrinter],
        'uuid',
      ),
    )

    forEach(allConfiugrationsForUpdate, (configuration) => {
      delete configuration.configuration.receiptPrinterUuid
    })

    forEach(allConfiugrationsForUpdate, (configuration) => {
      configuration.configuration.controlledPrinterUuids = filter(
        configuration.configuration.controlledPrinterUuids,
        (uuid) => uuid !== editedItem.uuid,
      )
    })

    if (isEmpty(allConfiugrationsForUpdate)) {
      deletePrinter(this.resourceId()).then(() => {
        toast.success('Printer successfully deleted')
        fetchPrinters(venueUuid).then(() => {
          this.redirectToPrinters()
        })
      })
    } else {
      Promise.all([
        deletePrinter(this.resourceId()),
        callConfiguration({
          appDeviceConfigurations: allConfiugrationsForUpdate,
        }),
      ]).then(() => {
        toast.success('Printer successfully deleted')
        fetchPrinters(venueUuid).then(() => {
          this.redirectToPrinters()
        })
        this.redirectToPrinters()
      })
    }
  }

  handleAutocompleteInputChange = (e) => {
    this.setState({
      [e.target.name]: [
        {
          label: e.target.value,
        },
      ],
    })
  }

  handleAutocompleteDropdownChange = (name, val) => {
    this.setState(
      {
        [name]: val,
      },
      () => this.getConfiguration(),
    )
  }

  getConfiguration = () => {
    const { printController } = this.state
    const { configurations } = this.props
    const configuration = find(
      configurations,
      (configuration) => configuration.uuid === head(printController).value,
    )

    this.setState({
      printController: [
        {
          label: get(configuration, 'name', ''),
          value: get(configuration, 'uuid', ''),
        },
      ],
    })
  }

  toggleModal = () =>
    this.setState((prevState) => ({
      isModalOpen: !prevState.isModalOpen,
    }))

  render() {
    const {
      name,
      printerType,
      printerModel,
      connection,
      printController,
      port,
      backupPort,
      receiptPrinter,
      isModalOpen,
    } = this.state

    const {
      title,
      printersUsbBluetoothTcp,
      editedItem,
      configurations,
      editedItem: { configurationsWithSetReceiptPrinterName },
    } = this.props

    const noBackupPrinterOption = !isEmpty(backupPort)
      ? { label: 'No backup printer', value: '' }
      : null

    const filteredPrintersWithUsbBluetoothTcp = filter(
      printersUsbBluetoothTcp,
      (printer) => printer.uuid !== editedItem.uuid,
    )

    const printersWithUsbBluetoothTcpOptions = compact([
      ...map(filteredPrintersWithUsbBluetoothTcp, (printer) => ({
        label: printer.name,
        value: printer.port,
      })),
      noBackupPrinterOption,
    ])

    const isPrinterBackup = some(
      filteredPrintersWithUsbBluetoothTcp,
      (printer) => printer.backupPort === port,
    )

    const printControllerOptions = isEmpty(printController[0].label)
      ? formattedAutocompleteList(configurations, 'Unamed configuration')
      : new Fuse(
          formattedAutocompleteList(configurations, 'Unamed configuration'),
          FUSE_OPTIONS,
        ).search(printController[0].label)

    return (
      <ItemsEdit onClose={this.redirectToPrinters} title={title}>
        <form onSubmit={this.handleSubmit}>
          <section className="form__fieldset">
            <Textbox
              label="Name"
              min={0}
              name="name"
              onChange={this.handleChange}
              required
              value={name}
            />
            <DropdownSelect
              className="-block"
              fullWidth
              isValid
              label="Printer type"
              name="printerType"
              onChange={this.handleChange}
              options={SELECTABLE_PRINTERS}
              required
              value={printerType}
            />
            <DropdownSelect
              className="-block"
              fullWidth
              isValid
              label="Printer Model"
              name="printerModel"
              onChange={this.handleChange}
              options={SELECTABLE_MODELS}
              required
              value={printerModel}
            />
            <div className="margin-top-base margin-bottom-base">
              <ToggleWithLabel
                checked={receiptPrinter}
                label="Receipt printer"
                name="receiptPrinter"
                onChange={this.handleCheckboxChange}
              />
            </div>
            <DropdownSelect
              className="-block"
              fullWidth
              isValid
              label="Connection"
              name="connection"
              onChange={this.handleConnectionChange}
              options={SELECTABLE_CONNECTIONS}
              required
              value={connection}
            />
            {connection === 'Bluetooth' && (
              <Textbox
                label="Bluetooth device ID"
                min={0}
                name="port"
                onChange={this.handleChange}
                required
                value={port}
              />
            )}
            {['TCP', ''].includes(connection) && (
              <Textbox
                label="IP Address"
                name="port"
                onChange={this.handleChange}
                onKeyPress={isNumberOrDot}
                required
                value={port}
              />
            )}
            <Autocomplete
              handleAutocompleteDropdownChange={this.handleAutocompleteDropdownChange}
              handleAutocompleteInputChange={this.handleAutocompleteInputChange}
              isRequired
              label="Print controller"
              name="printController"
              options={printControllerOptions}
              selectedOption={printController[0]}
            />
            <DropdownSelect
              className="-block"
              fullWidth
              isEmptyOption
              isValid
              label="Backup Printer "
              name="backupPort"
              onChange={this.handleChange}
              options={printersWithUsbBluetoothTcpOptions}
              value={backupPort}
            />
          </section>
          <section className="form__buttons">
            <button className="btn -lg -primary -block margin-bottom-base" type="submit">
              Save
            </button>
            {this.resourceId() && (
              <button className="btn -lg -outline -block" onClick={this.toggleModal} type="button">
                Delete Printer
              </button>
            )}
          </section>
        </form>
        <DeletePrinterModal
          configurationsWithSetReceiptPrinterName={configurationsWithSetReceiptPrinterName}
          deletePrinter={this.handleDeletePrinter}
          isBackup={isPrinterBackup}
          isModalOpen={isModalOpen}
          isPrinterExists={!isEmpty(this.resourceId())}
          toggleModal={this.toggleModal}
        />
      </ItemsEdit>
    )
  }
}

const mapStateToProps = (state, ownProps) => ({
  printersUsbBluetoothTcp: getUsbBluetoothTcpPrinters(state),
  editedItem: getPrinter(state, get(ownProps, ['match', 'params', 'id'])),
  venueUuid: getCurrentVenueId(state),
  configurations: getPrintControllerConfigurations(state),
  printers: getPrinters(state),
})

const mapDispatchToProps = {
  deletePrinter,
  fetchPrinters,
}

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps))(PrintersForm)
